import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ShieldCheck, Search, Filter, UserPlus, CreditCard as Edit3, History, X, AlertCircle, CheckCircle, Loader2, User as UserIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { critereDuPersonnel, type FiltreDeRole } from '../../../lib/utilisateurs/personnel'
import { db } from '../../../lib/payments/db'

type Role = 'admin' | 'coordi' | 'anim' | 'parent'

interface StaffUser {
  id: string
  email: string
  nom: string | null
  prenom: string | null
  role: Role | null
  created_at: string | null
}

interface HistoryEntry {
  id: string
  user_id: string
  old_role: string | null
  new_role: string
  changed_by: string | null
  reason: string | null
  changed_at: string
  target?: { prenom: string | null; nom: string | null; email: string } | null
  actor?: { prenom: string | null; nom: string | null; email: string } | null
}

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrateur',
  coordi: 'Coordinateur',
  anim: 'Animateur',
  parent: 'Parent',
}

const ROLE_BADGES: Record<Role, string> = {
  admin: 'bg-red-100 text-red-800 border-red-200',
  coordi: 'bg-blue-100 text-blue-800 border-blue-200',
  anim: 'bg-green-100 text-green-800 border-green-200',
  parent: 'bg-gray-100 text-gray-800 border-gray-200',
}

// Le filtre et la recherche partent a la base (`critereDuPersonnel`) : charger
// toute la table plafonnait a 1 000 lignes sans le dire.
const PAR_PAGE = 50

export function UsersManagement() {
  const [users, setUsers] = useState<StaffUser[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [recherche, setRecherche] = useState('')
  const [roleFilter, setRoleFilter] = useState<FiltreDeRole>('staff')
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [changing, setChanging] = useState<StaffUser | null>(null)
  const [editingIdentity, setEditingIdentity] = useState<StaffUser | null>(null)
  const [showHistory, setShowHistory] = useState(false)

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null))
  }, [])

  // Une requete quand la frappe s'arrete, pas une par lettre.
  useEffect(() => {
    const t = setTimeout(() => {
      setRecherche(search.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  // Seule la derniere requete lancee a le droit d'ecrire l'affichage.
  const derniere = useRef(0)
  const fetchUsers = useCallback(async () => {
    const mienne = ++derniere.current
    setLoading(true)
    setError(null)
    const { roles, filtres } = critereDuPersonnel(roleFilter, recherche)
    // `db` : le client type ne compile pas un `.in` sur `users` (voir payments/db.ts).
    let q = db
      .from('users')
      .select('id, email, nom, prenom, role, created_at', { count: 'exact' })
    if (roles) q = q.in('role', [...roles])
    for (const f of filtres) q = q.or(f)
    const debut = (page - 1) * PAR_PAGE
    const { data, error: qErr, count } = await q
      .order('role', { ascending: true })
      .order('nom', { ascending: true, nullsFirst: false })
      .order('prenom', { ascending: true, nullsFirst: false })
      .order('id', { ascending: true })
      .range(debut, debut + PAR_PAGE - 1)
    if (mienne !== derniere.current) return
    if (qErr) {
      setError(qErr.message)
      setLoading(false)
      return
    }
    setUsers((data ?? []) as StaffUser[])
    setTotal(count ?? 0)
    setLoading(false)
  }, [roleFilter, recherche, page])

  useEffect(() => {
    void fetchUsers()
  }, [fetchUsers])

  const pages = Math.max(1, Math.ceil(total / PAR_PAGE))

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
            Utilisateurs du personnel
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Gestion des roles administrateur, coordinateur et animateur.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowHistory(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <History className="w-4 h-4" />
            Historique
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <UserPlus className="w-4 h-4" />
            Nouveau compte
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, prenom ou email (parents inclus)"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value as FiltreDeRole)
                setPage(1)
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="staff">Personnel uniquement</option>
              <option value="all">Tous les utilisateurs</option>
              <option value="admin">Administrateurs</option>
              <option value="coordi">Coordinateurs</option>
              <option value="anim">Animateurs</option>
              <option value="parent">Parents</option>
            </select>
          </div>
        </div>
        {roleFilter === 'staff' && (
          <p className="mt-2 text-xs text-gray-500">
            Les comptes parents apparaissent uniquement lorsqu'une recherche d'au moins 2 caracteres est effectuee.
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">Aucun utilisateur trouve.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 tracking-wider">
                <tr>
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Cree le</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => {
                  const role = (u.role as Role | null) ?? null
                  const isSelf = u.id === currentUserId
                  return (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {u.prenom || u.nom ? `${u.prenom ?? ''} ${u.nom ?? ''}`.trim() : '(sans nom)'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{u.email}</td>
                      <td className="px-4 py-3">
                        {role ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${ROLE_BADGES[role]}`}>
                            {ROLE_LABELS[role]}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">non defini</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-BE') : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => setEditingIdentity(u)}
                            title="Modifier le nom et le prenom"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            <UserIcon className="w-3.5 h-3.5" />
                            Identite
                          </button>
                          <button
                            onClick={() => setChanging(u)}
                            disabled={isSelf}
                            title={isSelf ? 'Vous ne pouvez pas changer votre propre role' : 'Modifier le role'}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            Role
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-600">
            <span>
              {total} compte{total > 1 ? 's' : ''}
              {pages > 1 && ` · page ${page} / ${pages}`}
            </span>
            {pages > 1 && (
              <div className="inline-flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  title="Page precedente"
                  className="inline-flex items-center px-2 py-1 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page >= pages}
                  title="Page suivante"
                  className="inline-flex items-center px-2 py-1 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {changing && (
        <ChangeRoleModal
          user={changing}
          onClose={() => setChanging(null)}
          onSaved={() => {
            setChanging(null)
            void fetchUsers()
          }}
        />
      )}

      {editingIdentity && (
        <EditIdentityModal
          user={editingIdentity}
          onClose={() => setEditingIdentity(null)}
          onSaved={() => {
            setEditingIdentity(null)
            void fetchUsers()
          }}
        />
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            void fetchUsers()
          }}
        />
      )}

      {showHistory && <HistoryModal onClose={() => setShowHistory(false)} />}
    </div>
  )
}

function ChangeRoleModal({
  user, onClose, onSaved,
}: {
  user: StaffUser
  onClose: () => void
  onSaved: () => void
}) {
  const [newRole, setNewRole] = useState<Role>((user.role as Role) ?? 'anim')
  const [reason, setReason] = useState('')
  const [confirmName, setConfirmName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentRole = user.role as Role | null
  const isDemotingAdmin = currentRole === 'admin' && newRole !== 'admin'
  const expectedConfirm = (user.prenom || user.nom || user.email).trim()
  const canSubmit =
    newRole !== currentRole &&
    (!isDemotingAdmin || confirmName.trim().toLowerCase() === expectedConfirm.toLowerCase())

  const submit = async () => {
    setBusy(true)
    setError(null)
    const { error: rpcErr } = await supabase.rpc('admin_change_user_role', {
      target_user_id: user.id,
      new_role: newRole,
      reason: reason.trim() || null,
    })
    setBusy(false)
    if (rpcErr) {
      setError(rpcErr.message)
      return
    }
    onSaved()
  }

  return (
    <ModalShell title="Modifier le role" onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-3 text-sm">
          <div className="font-medium text-gray-900">
            {user.prenom} {user.nom}
          </div>
          <div className="text-gray-600 text-xs">{user.email}</div>
          <div className="mt-2 text-xs text-gray-500">
            Role actuel : <span className="font-medium">{currentRole ? ROLE_LABELS[currentRole] : 'non defini'}</span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Nouveau role</label>
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as Role)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="admin">Administrateur</option>
            <option value="coordi">Coordinateur</option>
            <option value="anim">Animateur</option>
            <option value="parent">Parent</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Motif (optionnel)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ex : promotion, changement de fonction, depart..."
          />
        </div>

        {isDemotingAdmin && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Vous retirez les droits administrateur de cet utilisateur. Pour confirmer, tapez son
                prenom, nom ou email : <span className="font-semibold">{expectedConfirm}</span>
              </span>
            </div>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Confirmation"
              className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white"
            />
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Enregistrer
          </button>
        </div>
      </div>
    </ModalShell>
  )
}

function CreateUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [prenom, setPrenom] = useState('')
  const [nom, setNom] = useState('')
  const [role, setRole] = useState<Role>('anim')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true)
    setError(null)
    const { data: sess } = await supabase.auth.getSession()
    const token = sess.session?.access_token
    if (!token) {
      setBusy(false)
      setError('Session expiree, veuillez vous reconnecter.')
      return
    }
    try {
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email, password, prenom, nom, role, reason: reason || null }),
        },
      )
      const payload = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        setError(payload?.error ?? 'Creation impossible')
        setBusy(false)
        return
      }
      setBusy(false)
      onCreated()
    } catch (e) {
      setBusy(false)
      setError(e instanceof Error ? e.message : 'Erreur reseau')
    }
  }

  const canSubmit =
    email.trim() && password.length >= 8 && prenom.trim() && nom.trim() && role

  return (
    <ModalShell title="Nouveau compte" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prenom">
            <input
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Field>
          <Field label="Nom">
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Field>
        </div>
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </Field>
        <Field label="Mot de passe (min. 8 caracteres)">
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
          />
        </Field>
        <Field label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="admin">Administrateur</option>
            <option value="coordi">Coordinateur</option>
            <option value="anim">Animateur</option>
          </select>
        </Field>
        <Field label="Motif (optionnel)">
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ex : embauche saison ete 2026"
          />
        </Field>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            L'utilisateur pourra se connecter avec cet email et ce mot de passe.
            Communiquez ces identifiants de facon securisee ; il pourra changer son mot de passe ensuite.
          </span>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Creer le compte
          </button>
        </div>
      </div>
    </ModalShell>
  )
}

function HistoryModal({ onClose }: { onClose: () => void }) {
  const [entries, setEntries] = useState<HistoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      const { data, error: qErr } = await supabase
        .from('role_change_history')
        .select(`
          id, user_id, old_role, new_role, changed_by, reason, changed_at,
          target:users!role_change_history_user_id_fkey (prenom, nom, email),
          actor:users!role_change_history_changed_by_fkey (prenom, nom, email)
        `)
        .order('changed_at', { ascending: false })
        .limit(500)
      if (qErr) setError(qErr.message)
      else setEntries((data ?? []) as unknown as HistoryEntry[])
      setLoading(false)
    })()
  }, [])

  return (
    <ModalShell title="Historique des changements de role" onClose={onClose} wide>
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">Aucun changement enregistre.</p>
      ) : (
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 sticky top-0">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Utilisateur</th>
                <th className="px-3 py-2">Avant</th>
                <th className="px-3 py-2">Apres</th>
                <th className="px-3 py-2">Par</th>
                <th className="px-3 py-2">Motif</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map((e) => {
                const oldR = e.old_role as Role | null
                const newR = e.new_role as Role
                return (
                  <tr key={e.id}>
                    <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                      {new Date(e.changed_at).toLocaleString('fr-BE')}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">
                        {e.target ? `${e.target.prenom ?? ''} ${e.target.nom ?? ''}`.trim() || e.target.email : '(supprime)'}
                      </div>
                      <div className="text-xs text-gray-500">{e.target?.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      {oldR ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${ROLE_BADGES[oldR]}`}>
                          {ROLE_LABELS[oldR]}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${ROLE_BADGES[newR]}`}>
                        {ROLE_LABELS[newR]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700">
                      {e.actor ? `${e.actor.prenom ?? ''} ${e.actor.nom ?? ''}`.trim() || e.actor.email : '-'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-xs">
                      {e.reason ?? '-'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </ModalShell>
  )
}

function ModalShell({
  title, onClose, children, wide,
}: {
  title: string
  onClose: () => void
  children: React.ReactNode
  wide?: boolean
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className={`bg-white rounded-xl shadow-xl w-full ${wide ? 'max-w-5xl' : 'max-w-lg'} max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  )
}

function EditIdentityModal({
  user, onClose, onSaved,
}: {
  user: StaffUser
  onClose: () => void
  onSaved: () => void
}) {
  const [prenom, setPrenom] = useState(user.prenom ?? '')
  const [nom, setNom] = useState(user.nom ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const changed = prenom.trim() !== (user.prenom ?? '').trim() || nom.trim() !== (user.nom ?? '').trim()

  const submit = async () => {
    setBusy(true)
    setError(null)
    const { error: uErr } = await supabase
      .from('users')
      .update({ prenom: prenom.trim() || null, nom: nom.trim() || null })
      .eq('id', user.id)
    setBusy(false)
    if (uErr) {
      setError(uErr.message)
      return
    }
    onSaved()
  }

  return (
    <ModalShell title="Modifier l'identite" onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-3 text-sm">
          <div className="text-gray-600 text-xs">{user.email}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prenom">
            <input
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Field>
          <Field label="Nom">
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Field>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!changed || busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Enregistrer
          </button>
        </div>
      </div>
    </ModalShell>
  )
}